import { useEffect, useState, useRef } from 'react';

export default function ValuePillars() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const pillars = [
    {
      icon: 'ri-building-line',
      title: 'Smart Listings',
      description: 'Verified properties with accurate pricing and complete transaction history.'
    },
    {
      icon: 'ri-dashboard-line',
      title: 'Property Management',
      description: 'Tenants, maintenance, income — all managed from one intuitive dashboard.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Valuation & Insights',
      description: 'Market-backed decisions powered by local data and real-time analytics.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Secure Transactions',
      description: 'Digital contracts and traceable payments for complete peace of mind.'
    }
  ];

  return (
    <section ref={sectionRef} className="py-32" style={{ backgroundColor: '#EAF0FF' }}>
      <div className="container mx-auto px-6 lg:px-12">
        <div className="text-center mb-20">
          <h2 className="text-5xl font-semibold mb-6" style={{ color: '#0B1C2D' }}>
            Built for Modern Real Estate
          </h2>
          <p className="text-xl max-w-2xl mx-auto" style={{ color: '#6B7A99' }}>
            Everything you need to manage, value, and transact property in one powerful platform
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map((pillar, index) => (
            <div
              key={index}
              className={`group p-8 rounded-2xl transition-all duration-700 cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(14px)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                boxShadow: '0 4px 24px rgba(30, 79, 255, 0.06)',
                transitionDelay: `${index * 100}ms`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(30, 79, 255, 0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 24px rgba(30, 79, 255, 0.06)';
              }}
            >
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-all duration-300"
                style={{ 
                  backgroundColor: 'rgba(30, 79, 255, 0.1)',
                  border: '1px solid rgba(30, 79, 255, 0.2)'
                }}
              >
                <i className={`${pillar.icon} text-3xl`} style={{ color: '#1E4FFF' }}></i>
              </div>
              
              <h3 className="text-2xl font-semibold mb-4" style={{ color: '#0B1C2D' }}>
                {pillar.title}
              </h3>
              
              <p className="text-base leading-relaxed" style={{ color: '#6B7A99' }}>
                {pillar.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
